import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Gift, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { pluralHintWord } from './ViralityPage';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Simple module-level cache: locale -> {bannerInvite, ...} so the lookup only
// hits the API once per session per locale.
const bannerTextCache = {};

/**
 * Inline FOMO banner shown above the home-page logo to a visitor that landed
 * via a referral link. Reads referral_* from localStorage (set by
 * RefLanding.js). Auto-hides when user is already authenticated OR the
 * 10-minute window expires OR the user dismisses it.
 */
export default function ReferralBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(false);
  const [data, setData] = useState(null);
  const [adminText, setAdminText] = useState(bannerTextCache[language] || null);

  // Pull admin-edited banner copy from the DB. Falls back gracefully if the
  // endpoint is unreachable or the DB has no record — the i18n locale file
  // serves as a safety net (via the `?? t(...)` chains below).
  useEffect(() => {
    if (bannerTextCache[language]) {
      setAdminText(bannerTextCache[language]);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/api/referral-banner/template`, { params: { locale: language } })
      .then(r => {
        if (cancelled) return;
        bannerTextCache[language] = r.data || null;
        setAdminText(r.data || null);
      })
      .catch(() => { if (!cancelled) setAdminText(null); });
    return () => { cancelled = true; };
  }, [language]);

  // Helper: prefer admin-edited text, fall back to the i18n key.
  const txt = (key, params) => {
    if (adminText && adminText[key]) {
      let out = String(adminText[key]);
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
        }
      }
      return out;
    }
    return t(`virality.${key}`, params);
  };

  useEffect(() => {
    const load = () => {
      const slug = localStorage.getItem('referral_slug');
      const expiresAt = parseInt(localStorage.getItem('referral_expires_at') || '0', 10);
      if (!slug || !expiresAt) return;
      setData({
        slug,
        inviter: localStorage.getItem('referral_inviter') || t('virality.bellAnonymous'),
        welcomePoints: parseInt(localStorage.getItem('referral_welcome_points') || '0', 10),
        hintCost: parseInt(localStorage.getItem('referral_hint_cost') || '30', 10),
        expiresAt,
      });
    };
    load();
    const handler = () => load();
    window.addEventListener('referral-stored', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('referral-stored', handler);
      window.removeEventListener('storage', handler);
    };
  }, [t]);

  useEffect(() => {
    if (!data || dismissed) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [data, dismissed]);

  useEffect(() => {
    if (user) {
      localStorage.removeItem('referral_slug');
      localStorage.removeItem('referral_inviter');
      localStorage.removeItem('referral_welcome_points');
      localStorage.removeItem('referral_hint_cost');
      localStorage.removeItem('referral_expires_at');
    }
  }, [user]);

  if (!data || user || dismissed) return null;
  const msLeft = data.expiresAt - now;
  if (msLeft <= 0) return null;

  const mm = Math.floor(msLeft / 60000);
  const ss = Math.floor((msLeft % 60000) / 1000).toString().padStart(2, '0');
  const timer = `${mm}:${ss}`;
  const hints = data.hintCost > 0 ? Math.floor(data.welcomePoints / data.hintCost) : 0;

  return (
    <div data-testid="referral-banner" style={{
      position: 'relative',
      width: '100%',
      maxWidth: 720,
      margin: '0 auto 1.5rem',
      padding: '14px 18px',
      borderRadius: 16,
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.22) 0%, rgba(30, 95, 184, 0.32) 50%, rgba(10, 71, 151, 0.35) 100%)',
      border: '1px solid rgba(147, 197, 253, 0.35)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      flexWrap: 'wrap',
      textAlign: 'left',
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 12,
        background: 'rgba(255,255,255,0.16)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Gift size={22} />
      </div>
      <div style={{ flex: 1, lineHeight: 1.45, minWidth: 200, fontSize: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }} data-testid="referral-banner-inviter">
          {txt('bannerInvite', { name: data.inviter })}
        </div>
        <div style={{ opacity: 0.95 }}>
          {txt('bannerCountdown', { timer: '__TIMER__', points: data.welcomePoints })
            .split('__TIMER__')
            .map((chunk, i, arr) => (
              <React.Fragment key={i}>
                {chunk}
                {i < arr.length - 1 && (
                  <b style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'rgba(255,255,255,0.18)', padding: '2px 8px',
                    borderRadius: 6, fontVariantNumeric: 'tabular-nums',
                  }}>
                    <Clock size={12} /> {timer}
                  </b>
                )}
              </React.Fragment>
            ))}
          {hints > 0 && txt('bannerHints', { n: hints, hintWord: pluralHintWord(hints, language, t) })}
        </div>
      </div>
      <button
        data-testid="referral-banner-cta"
        onClick={() => navigate('/register')}
        style={{
          background: 'white', color: '#0a4797', border: 'none', borderRadius: 10,
          padding: '10px 18px', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
          fontSize: 13, whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        {txt('bannerCTA')}
      </button>
      <button
        data-testid="referral-banner-close"
        onClick={() => setDismissed(true)}
        aria-label={t('common.close')}
        style={{
          position: 'absolute', top: 8, right: 10,
          background: 'transparent', color: 'rgba(255,255,255,0.7)', border: 'none',
          cursor: 'pointer', padding: 4,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
